import { Navigate, useLocation } from "react-router-dom";
import { getResolvedUserSidebar, isSuperAdminRole, normalizeSidebarSlug } from "./helper";

const ProtectedRoute = ({ permission = "view", children }) => {
  const userDetails = JSON.parse(localStorage.getItem("fms-maintenance-details"));
  const location = useLocation();
  const resolvedSidebar = getResolvedUserSidebar(userDetails?.user_sidebar);
  const normalizePermission = (value) => normalizeSidebarSlug(value);

  // Check if user is authenticated at all
  if (!userDetails) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // SUPER ADMIN OVERRIDE
  if (isSuperAdminRole(userDetails?.my_role)) {
    return children;
  }

  // Training Library is accessible to all authenticated users
  const currentPath = normalizeSidebarSlug(location.pathname);
  if (currentPath.startsWith("training-library")) {
    return children;
  }

  // Regular users MUST have sidebar data
  if (resolvedSidebar.length === 0) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  const pathSegment = currentPath.split('-')[0] || currentPath;

  // Find if user has permission for this path
  const hasAccess = resolvedSidebar.some(module => {
    return module.menus?.some(menu => {
      const menuSlug = normalizeSidebarSlug(menu?.slug || menu?.url || menu?.name);
      const isPettyCashPath = currentPath.includes("petty-cash") || currentPath === "petty-cash";
      const isExpensePath = currentPath.includes("expense") || currentPath === "trip-expenses" || currentPath === "trip-expense";
      const isFuelRequestPath = currentPath.includes("fuel-request");

      let slugMatches = false;
      if (isPettyCashPath) {
        slugMatches = menuSlug === "petty-cash" || menuSlug === "pettycash";
      } else if (isExpensePath) {
        slugMatches =
          menuSlug === "trip-expenses" ||
          menuSlug === "trip-expense" ||
          menuSlug === "expenses" ||
          menuSlug === "tripexpenses";
      } else if (isFuelRequestPath) {
        slugMatches =
          menuSlug === "fuel-requests" ||
          menuSlug === "fuel-request" ||
          menuSlug === "fuelrequests";
      } else {
        slugMatches =
          menuSlug === currentPath ||
          currentPath.startsWith(`${menuSlug}-`) ||
          ((menuSlug === "trips" || menuSlug === "trips-list" || menuSlug === "trip-management" || menuSlug === "trip") &&
            (currentPath === "trips" || currentPath === "trips-list" || currentPath.startsWith("trips-list") || currentPath.startsWith("trips"))) ||
          ((menuSlug === "fuel" || menuSlug === "fuel-management" || menuSlug === "fuel-logs") &&
            (currentPath === "fuel" || currentPath.startsWith("fuel-logs") || currentPath.startsWith("fuel"))) ||
          (menuSlug === "transporters" && currentPath.startsWith("transporters")) ||
          (menuSlug === "transporters" && currentPath.startsWith("3pl-transporter")) ||
          (menuSlug === "customers" && currentPath.startsWith("customers")) ||
          (menuSlug === "customers" && currentPath.startsWith("3pl-customers")) ||
          (menuSlug === "pricing-templates" && currentPath.startsWith("contract")) ||
          (menuSlug === "vendor-warehouse" &&
            ["vendor", "warehouse", "vendor-warehouse"].includes(pathSegment)) ||
          (menuSlug === "procurement" && (currentPath.startsWith("rfqs") || currentPath.startsWith("po"))) ||
          (currentPath === "" && menuSlug === "dashboard") ||
          (currentPath.startsWith("fmcr") && (menuSlug === "fmcr" || menuSlug.includes("trip"))) ||
          (["roles", "role-management", "app-modules", "access-management"].includes(currentPath) &&
            ["access-management", "roles", "role-management", "user-management", "access", "app-modules"].some((s) => menuSlug.includes(s)));
      }

      if (!slugMatches) return false;

      // For "any" permission, check if user has any permissions for this path
      if (permission === "any" && menu.permissions && menu.permissions.length > 0) {

        return true;
      }

      // Support array of permissions (OR condition)
      if (Array.isArray(permission)) {
        const normalizedPermissions = permission.map(normalizePermission);
        return menu.permissions && menu.permissions.some((perm) => {
          const candidates = [
            normalizePermission(perm?.slug),
            normalizePermission(perm?.name),
          ].filter(Boolean);
          return candidates.some((candidate) => normalizedPermissions.includes(candidate));
        });
      }

      // For regular permissions, check if user has the required permission for this menu item
      return menu.permissions &&
        menu.permissions.some((perm) => {
          const normalizedRequiredPermission = normalizePermission(permission);
          return (
            normalizePermission(perm?.slug) === normalizedRequiredPermission ||
            normalizePermission(perm?.name) === normalizedRequiredPermission
          );
        });
    });
  });

  return hasAccess ? children : <Navigate to="/no-access" replace />;
};

export default ProtectedRoute;
